<script>
import { visitUrl, getParameterByName, updateHistory, removeParams } from '~/lib/utils/url_utility';
import { TYPENAME_WORK_ITEM, TYPENAME_NAMESPACE } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import { convertToTitleCase } from '~/lib/utils/text_utility';
import CreateWorkItem from '../components/create_work_item.vue';
import CreateWorkItemCancelConfirmationModal from '../components/create_work_item_cancel_confirmation_modal.vue';
import {
  ROUTES,
  RELATED_ITEM_ID_URL_QUERY_PARAM,
  CREATION_CONTEXT_NEW_ROUTE,
  WORK_ITEM_TYPE_NAME_EPIC,
  WORK_ITEM_TYPE_NAME_INCIDENT,
  WORK_ITEM_TYPE_ENUM_INCIDENT,
} from '../constants';
import workItemRelatedItemQuery from '../graphql/work_item_related_item.query.graphql';
import namespaceWorkItemTypesQuery from '../graphql/namespace_work_item_types.query.graphql';
import { getDraftWorkItemType } from '../utils';

export default {
  CREATION_CONTEXT_NEW_ROUTE,
  name: 'CreateWorkItemPage',
  components: {
    CreateWorkItem,
    CreateWorkItemCancelConfirmationModal,
  },
  inject: ['isGroup'],
  props: {
    rootPageFullPath: {
      type: String,
      required: true,
    },
  },
  data() {
    // Generated by backend with add_related_issue query parameter
    const relatedId = document.querySelector('.params-add-related-issue')?.textContent.trim();
    const legacyAddRelatedIssueId = relatedId
      ? convertToGraphQLId(TYPENAME_WORK_ITEM, relatedId)
      : undefined;

    return {
      relatedItem: null,
      relatedItemId: getParameterByName(RELATED_ITEM_ID_URL_QUERY_PARAM) || legacyAddRelatedIssueId,
      isCancelConfirmationModalVisible: false,
      shouldDiscardDraft: false,
      workItemType: null,
      // Update creationContext based on `initialCreationContext` params
      // so that users can continue from same state when they switch to full page from create new work item modal
      creationContext:
        getParameterByName('initialCreationContext') || this.$options.CREATION_CONTEXT_NEW_ROUTE,
    };
  },
  apollo: {
    relatedItem: {
      query: workItemRelatedItemQuery,
      variables() {
        return {
          id: this.relatedItemId,
        };
      },
      skip() {
        return !this.relatedItemId;
      },
      update({ workItem }) {
        return {
          id: this.relatedItemId,
          reference: workItem.reference,
          type: workItem.workItemType.name,
          webUrl: workItem.webUrl,
        };
      },
      error() {
        // if we cannot find an item with the given id, ignore it and remove it from the url.
        updateHistory({ url: removeParams([RELATED_ITEM_ID_URL_QUERY_PARAM]), replace: true });
      },
    },
  },
  computed: {
    isEpic() {
      return this.workItemType === WORK_ITEM_TYPE_NAME_EPIC;
    },
    isIncident() {
      return this.workItemType === WORK_ITEM_TYPE_NAME_INCIDENT;
    },
    isNewGroupWorkItem() {
      return !this.isEpic && this.isGroup;
    },
    preselectedWorkItemType() {
      const normalizeTypeName = (name) =>
        convertToTitleCase(name.toLowerCase().replaceAll('_', ' '));

      // The `type` parameter is not user-documented, but is the SSoT way to set the type
      const typeParameter = getParameterByName('type') ?? '';
      // The `issue[issue_type]` parameter is documented from legacy issues so we should continue supporting it.
      // `.params-issue-type` holds a value set by the backend which is only `issue` or `incident`
      const issueTypeParameter =
        (getParameterByName('issue[issue_type]') &&
          document.querySelector('.params-issue-type')?.textContent.trim()) ??
        '';
      const draftType =
        getDraftWorkItemType({
          fullPath: this.rootPageFullPath,
          context: CREATION_CONTEXT_NEW_ROUTE,
        })?.name ?? '';

      return normalizeTypeName(typeParameter || issueTypeParameter || draftType);
    },
  },
  methods: {
    updateWorkItemType(type) {
      this.workItemType = type;
    },
    workItemCreated({ workItem, numberOfDiscussionsResolved }) {
      if (this.$router && !this.isIncident && !this.isNewGroupWorkItem) {
        const routerPushObject = {
          name: ROUTES.workItem,
          params: { iid: workItem.iid },
        };
        if (numberOfDiscussionsResolved) {
          routerPushObject.query = { resolves_discussion: numberOfDiscussionsResolved };
        }

        const { cache } = this.$apollo.provider.defaultClient;
        const cachedData = cache.readQuery({
          query: namespaceWorkItemTypesQuery,
          variables: { fullPath: this.rootPageFullPath },
        });

        if (cachedData?.namespace?.id) {
          cache.evict({
            id: cache.identify({ __typename: TYPENAME_NAMESPACE, id: cachedData.namespace.id }),
            fieldName: 'workItems',
          });
          cache.gc();
        }

        this.$router.push(routerPushObject);
      } else {
        visitUrl(workItem.webUrl);
      }
    },
    handleCancelClick() {
      if (getParameterByName('type') === WORK_ITEM_TYPE_ENUM_INCIDENT) {
        this.$router.go(-1);
      } else {
        this.$router.push({ name: ROUTES.index });
      }
    },
    hideConfirmationModal() {
      this.isCancelConfirmationModalVisible = false;
    },
    showConfirmationModal() {
      this.isCancelConfirmationModalVisible = true;
    },
    handleConfirmCancellation() {
      this.showConfirmationModal();
    },
    handleContinueEditing() {
      this.shouldDiscardDraft = false;
      this.hideConfirmationModal();
    },
    handleDiscardDraft() {
      this.hideConfirmationModal();
      this.handleCancelClick();
      // trigger discard draft function on create work item component
      this.shouldDiscardDraft = true;
    },
  },
};
</script>

<template>
  <div>
    <create-work-item
      :creation-context="creationContext"
      :full-path="rootPageFullPath"
      :preselected-work-item-type="preselectedWorkItemType"
      :is-group="isGroup"
      :related-item="relatedItem"
      :should-discard-draft="shouldDiscardDraft"
      :always-show-work-item-type-select="!isEpic"
      :show-project-selector="isNewGroupWorkItem"
      @update-type="updateWorkItemType($event)"
      @confirm-cancel="handleConfirmCancellation"
      @discard-draft="handleDiscardDraft('createPage')"
      @work-item-created="workItemCreated"
    />
    <create-work-item-cancel-confirmation-modal
      v-if="workItemType"
      :is-visible="isCancelConfirmationModalVisible"
      :work-item-type="workItemType"
      @continue-editing="handleContinueEditing"
      @discard-draft="handleDiscardDraft('confirmModal')"
    />
  </div>
</template>
